import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { promptsAPI, leaderboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import toast from 'react-hot-toast';
import { FaTrophy, FaMedal, FaStar, FaFire, FaUser, FaLock } from 'react-icons/fa';

const Leaderboard = () => {
  const { currentUser } = useAuth();
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [weeklyPrompts, setWeeklyPrompts] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('prompts'); // 'prompts' or 'users'

  useEffect(() => {
    fetchLeaderboardData();
  }, []);

  const fetchLeaderboardData = async () => {
    setLoading(true);
    try {
      // Fetch weekly top prompts
      const promptsResponse = await promptsAPI.getWeeklyTop(10);
      setWeeklyPrompts(promptsResponse.data.prompts || []);

      // Fetch top users
      const usersResponse = await leaderboardAPI.get({
        orderBy: 'total_boops_received',
        limit: 10,
      });
      setTopUsers(usersResponse.data.users || []);
    } catch (error) {
      console.error('Fetch leaderboard error:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (position) => {
    switch (position) {
      case 0:
        return <FaTrophy className="text-yellow-500 text-2xl" />;
      case 1:
        return <FaMedal className="text-gray-400 text-2xl" />;
      case 2:
        return <FaMedal className="text-orange-600 text-2xl" />;
      default:
        return <span className="text-xl font-bold text-gray-500">#{position + 1}</span>;
    }
  };

  const getMedalBg = (position) => {
    if (darkMode) {
      switch (position) {
        case 0:
          return 'bg-gradient-to-r from-yellow-900/30 to-yellow-800/20 border-2 border-yellow-600';
        case 1:
          return 'bg-gradient-to-r from-gray-800/50 to-gray-700/30 border-2 border-gray-500';
        case 2:
          return 'bg-gradient-to-r from-orange-900/30 to-orange-800/20 border-2 border-orange-600';
        default:
          return 'bg-gray-800';
      }
    } else {
      switch (position) {
        case 0:
          return 'bg-gradient-to-r from-yellow-100 to-yellow-50 border-2 border-yellow-400';
        case 1:
          return 'bg-gradient-to-r from-gray-100 to-gray-50 border-2 border-gray-400';
        case 2:
          return 'bg-gradient-to-r from-orange-100 to-orange-50 border-2 border-orange-400';
        default:
          return 'bg-white';
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center py-12">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <FaTrophy className="text-6xl text-yellow-500" />
        </div>
        <h1 className="text-4xl font-bold mb-2">Leaderboard</h1>
        <p className="text-gray-600 text-lg">
          Celebrating the best prompts and top contributors this week
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className={`rounded-lg shadow-md p-1 inline-flex ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-6 py-2 rounded-lg font-semibold transition ${
              activeTab === 'prompts'
                ? 'bg-boop-primary text-white'
                : 'text-gray-600 hover:text-boop-primary'
            }`}
          >
            <FaFire className="inline mr-2" />
            Top Prompts
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-2 rounded-lg font-semibold transition ${
              activeTab === 'users'
                ? 'bg-boop-primary text-white'
                : 'text-gray-600 hover:text-boop-primary'
            }`}
          >
            <FaUser className="inline mr-2" />
            Top Users
          </button>
        </div>
      </div>

      {/* Top Prompts Tab */}
      {activeTab === 'prompts' && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">This Week's Top Prompts</h2>
            <p className="text-gray-600">
              Based on ratings and engagement from the past 7 days
            </p>
          </div>

          {weeklyPrompts.length === 0 ? (
            <div className={`p-12 rounded-lg shadow-md text-center ${
              darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'
            }`}>
              No prompts this week yet. Be the first to post!
            </div>
          ) : (
            <div className="space-y-4">
              {weeklyPrompts.map((prompt, index) => (
                <div
                  key={prompt.id}
                  className={`relative block p-6 rounded-lg shadow-md ${
                    currentUser ? 'hover:shadow-xl' : ''
                  } transition ${getMedalBg(index)}`}
                  onClick={() => {
                    if (currentUser) {
                      navigate(`/prompts/${prompt.id}`);
                    }
                  }}
                  style={{ cursor: currentUser ? 'pointer' : 'default' }}
                >
                  <div className={`flex items-start gap-4 ${!currentUser ? 'filter blur-sm' : ''}`}>
                    {/* Position/Medal */}
                    <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center">
                      {getMedalIcon(index)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-semibold mb-2 line-clamp-1">
                        {prompt.title}
                      </h3>
                      <p className="text-gray-600 mb-3 line-clamp-2">
                        {prompt.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="text-boop-primary font-medium">
                          @{prompt.username}
                        </span>
                        <span className="flex items-center text-yellow-600 font-semibold">
                          <FaStar className="mr-1" />
                          {(parseFloat(prompt.bayesian_rating) || 0).toFixed(1)}
                        </span>
                        <span className="text-gray-500">
                          {prompt.total_ratings || 0} ratings
                        </span>
                        <span className="text-gray-500">
                          {prompt.views_count || 0} views
                        </span>
                      </div>

                      <div className="flex gap-2 flex-wrap mt-3">
                        {prompt.tags?.slice(0, 3).map((tag, i) => (
                          <span
                            key={i}
                            className={`px-2 py-1 text-xs rounded-full ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex-shrink-0 text-right">
                      <div className="text-3xl font-bold text-boop-primary mb-1">
                        {(parseFloat(prompt.bayesian_rating) || 0).toFixed(1)}
                      </div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                  </div>

                  {/* Login Overlay for Non-Authenticated Users */}
                  {!currentUser && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/10 backdrop-blur-[2px] rounded-lg">
                      <div className="text-center">
                        <FaLock className="text-4xl text-boop-primary mx-auto mb-3" />
                        <p className="text-lg font-semibold text-gray-800 mb-2">
                          Login to View
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/login');
                          }}
                          className="bg-boop-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-boop-secondary transition"
                        >
                          Sign In
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">Top Contributors</h2>
            <p className="text-gray-600">
              Users with the most boops received on their prompts
            </p>
          </div>

          {topUsers.length === 0 ? (
            <div className={`p-12 rounded-lg shadow-md text-center ${
              darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'
            }`}>
              No users yet. Be the first to contribute!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topUsers.map((user, index) => (
                <Link
                  key={user.id}
                  to={`/profile/${user.username}`}
                  className={`block p-6 rounded-lg shadow-md hover:shadow-xl transition ${
                    index < 3 ? getMedalBg(index) : darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}
                >
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 mr-3">
                      {index < 3 ? (
                        <div className="w-12 h-12 flex items-center justify-center">
                          {getMedalIcon(index)}
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-boop-light rounded-full flex items-center justify-center text-xl font-bold text-boop-primary">
                          {user.username?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-lg truncate">
                        {user.display_name || user.username}
                      </div>
                      <div className="text-sm text-gray-500">@{user.username}</div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Level:</span>
                      <span className="font-semibold text-boop-primary text-lg">
                        {user.level || 'B'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prompts:</span>
                      <span className="font-semibold">{user.prompts_count || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Boops Received:</span>
                      <span className="font-semibold text-boop-secondary">
                        {user.total_boops_received || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Boops Given:</span>
                      <span className="font-semibold">{user.total_boops_given || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CTA Section */}
      <div className="mt-12 bg-gradient-to-r from-boop-primary to-boop-secondary text-white rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-3">Want to See Your Name Here?</h2>
        <p className="text-lg mb-6">
          Create amazing prompts, get ratings, and climb the leaderboard!
        </p>
        <Link
          to="/prompts/create"
          className={`inline-block px-8 py-3 rounded-lg font-semibold transition ${
            darkMode
              ? 'bg-gray-800 text-purple-400 hover:bg-gray-700'
              : 'bg-white text-boop-primary hover:bg-gray-100'
          }`}
        >
          Create Your Prompt
        </Link>
      </div>
    </div>
  );
};

export default Leaderboard;
